/**
 * /technical-design-document — SEO landing page targeting the "technical
 * design document" / "technical design document template" / "design doc
 * template" / "software design document" cluster. Audience skews
 * engineering: the person who has to write the TDD before they build.
 *
 * Differentiation angle: a technical design document is the bridge between
 * a PRD ("what / why") and the implementation ("how"). The practice is
 * right — a short doc that pins down architecture, data model, API, and
 * trade-offs before code. The trouble is the substrate: a TDD is markdown
 * in the repo or a page in Confluence, written once against a PRD that
 * keeps moving. The PRD changes, nobody re-syncs the design doc, and the
 * implementation drifts from a spec the team still believes. This page
 * hands the reader the actual TDD template (the eight sections, spelled
 * out) so a searcher gets something usable, then shows that Draftlize
 * turns each section into a card linked to the upstream decisions — so the
 * design doc flips to drift_detected the moment the spec moves, and an AI
 * coding agent (Claude Code / Cursor via MCP) always reads the current
 * design.
 *
 * PRD vs TDD distinction is explicit throughout: PRD = what / why,
 * TDD = how the architecture and implementation are designed.
 *
 * English-only + inline (no i18n), mirroring DecisionLogTemplate.tsx and
 * the comparison pages. Chinese variant lives in
 * TechnicalDesignDocumentZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function TechnicalDesignDocument() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/technical-design-document"
      documentTitle="Technical Design Document Template (That Stays in Sync) · Draftlize"
      documentDescription="A technical design document template you can copy — Overview, Goals & non-goals, Proposed architecture, Data model, API design, Key trade-offs, Risks & mitigations, Rollout plan. The catch: a design doc in the repo or Confluence is stale the moment the PRD moves past it. In Draftlize every section is a card linked to the upstream decisions, so the TDD flips to drift_detected when the spec changes — and a coding agent reads the current design over MCP."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Technical Design Docs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A technical design document<br/>
            <em>that flips to drift_detected<br/>when the spec moves.</em>
          </h1>
          <p className="lede">
            You came for a <em>technical design document template</em> — the sections, the headings, something to paste in before you start building. It's below; take it. But know the failure mode you're inheriting: a TDD is written once against a PRD, and the PRD never stops moving. Someone reframes the requirement, the design doc in <code>docs/design/</code> or on a Confluence page doesn't move with it, and the implementation drifts from a spec the whole team still trusts. Draftlize gives you the same eight sections — and makes each one a card linked to the decisions it rests on, so the moment an upstream call changes, the design doc flips to <em>drift_detected</em> and your coding agent reads the corrected design, not the stale one.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Eight sections. <em>Copy them anywhere.</em></h2>
          </div>
          <p>This is the whole template — a software design document in the shape engineers actually use. It works in a markdown file, a wiki, or a Google Doc. Use it as-is. The next section is about what changes when each heading stops being prose under a PRD nobody re-reads and becomes a card that knows when its premises moved.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Overview',
              hint: 'The one-pager',
              body: 'What you\'re building and the one-line problem it solves, with a link up to the PRD that asked for it. The PRD says what and why; this doc starts where that leaves off and says how.',
            },
            {
              k: 'Goals & non-goals',
              hint: 'Scope',
              body: 'What this design is responsible for — and, just as load-bearing, what it deliberately is not. The non-goals are the line you point at six weeks later when someone asks why the system doesn\'t do the thing it was never meant to.',
            },
            {
              k: 'Proposed architecture',
              hint: 'The shape',
              body: 'The components, how they talk, and where the boundaries are. A diagram and the prose that defends it. This is the section a reviewer reads first and the one most likely to be quietly invalidated when an upstream decision changes.',
            },
            {
              k: 'Data model',
              hint: 'The schema',
              body: 'The entities, their fields, the relationships, and the migrations. Where state lives and who owns it. Get this wrong and every API below inherits the mistake — which is why it sits above the API section, not beside it.',
            },
            {
              k: 'API design',
              hint: 'The contract',
              body: 'Endpoints or interfaces, request and response shapes, error semantics, and versioning. The contract other teams build against — so it has to stay honest about what the system actually exposes, not what it exposed at design time.',
            },
            {
              k: 'Key trade-offs',
              hint: 'The forks',
              body: 'The alternatives you weighed and the one you chose, with the reason. The section your future self opens to understand why it\'s built this way. Most TDDs skip it; it\'s the part that ages best when it\'s there.',
            },
            {
              k: 'Risks & mitigations',
              hint: 'What could break',
              body: 'The failure modes you can foresee — scale, consistency, the dependency that might not hold — and what you\'ll do about each. Honest risk is cheaper to write here than to discover in an incident.',
            },
            {
              k: 'Rollout plan',
              hint: 'How it ships',
              body: 'Phases, feature flags, the migration order, and the rollback. How this design becomes a running system without a big-bang cutover — and how you back out if a premise turns out wrong in production.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the template. Below: why it drifts from the PRD the day after you write it — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">PRD, then TDD — <em>and what's the difference?</em></h2>
        </div>
        <p style={{ maxWidth: 720, color: 'var(--ink-1)', fontSize: 16, lineHeight: 1.65, margin: '0 0 32px' }}>
          People reach for "design doc" and "PRD" interchangeably, and that's exactly how implementations go sideways. They answer different questions, and the TDD is downstream of the PRD — which is why it's the one that silently rots when the PRD moves.
        </p>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">PRD: what and why</h3>
            <p>A product requirements doc is about the problem — the user, the outcome, the success metric, the constraints. It deliberately doesn&rsquo;t say how. &ldquo;Users can recover an account without contacting support&rdquo; is a PRD line. It says nothing about tokens, tables, or endpoints, on purpose.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">TDD: how it&rsquo;s designed</h3>
            <p>A technical design document is about the solution — the architecture, the data model, the API, the trade-offs that get you from that requirement to running code. It&rsquo;s where &ldquo;recover without support&rdquo; becomes a signed token, a <code>password_reset</code> table, and a rate-limited endpoint with a rollout plan.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">The TDD inherits the PRD&rsquo;s every move</h3>
            <p>Because the design is built on the requirement, every change to the PRD ripples down. Tighten the requirement to &ldquo;recover within 60 seconds, no email&rdquo; and the token-and-email design is now wrong — but the design doc doesn&rsquo;t know that, and nobody re-opens it to find out.</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a design doc <em>drifts from the spec.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Written once, against a moving target</h3>
            <p>The TDD is most accurate the hour you finish it — pinned to the PRD as it read that day. But the PRD keeps moving: a requirement gets reframed in a meeting, a constraint loosens, a decision upstream flips. The design doc stays frozen at the moment of writing while the thing it was designed against walks away.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Nothing links a section to what it assumes</h3>
            <p>Your Proposed architecture rests on a decision — &ldquo;we&rsquo;re multi-tenant,&rdquo; &ldquo;we don&rsquo;t need real-time.&rdquo; In markdown that assumption is a sentence, not a link. When the decision changes, no signal reaches the design doc, so the architecture quietly describes a system you&rsquo;re no longer going to build.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Re-syncing loses to shipping</h3>
            <p>Updating the design doc to match the new PRD is a chore that competes with writing the code, and the code always wins. So <code>docs/design/</code> freezes, the implementation tracks the conversation in the PR instead, and the gap between the documented design and the running system widens every sprint.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same template, <em>kept in sync.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each section becomes a card, not a heading</h3>
            <p>In Draftlize the TDD isn&rsquo;t a wall of prose — each section is a typed card: Overview, Goals &amp; non-goals, Proposed architecture, Data model, API design, Key trade-offs, Risks &amp; mitigations, Rollout plan. Structured, addressable by ID, and citable from a thread or another spec instead of buried in a Confluence tree.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The PRD moves, the TDD flags drift_detected</h3>
            <p>Each section links to the upstream PRD lines and decisions it rests on. Change one of those, and every design card built on it flips to <em>drift_detected</em> — the way a build system invalidates everything downstream of a changed file. The design doc tells you it&rsquo;s out of sync the moment it is, instead of misleading the next reader.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">A coding agent reads the current design</h3>
            <p>Claude Code or Cursor, over MCP, pulls the relevant design cards before it writes code — and because drift surfaces, it reads the corrected architecture, not the version that matched last month&rsquo;s PRD. The design doc stops being a write-once artifact and becomes context the agent reads on every turn.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A design doc is a bet on a spec that won&rsquo;t hold still. Stop re-syncing it by hand; let it flag itself when the spec moves.
        </blockquote>
        <cite>Keep the eight sections. Link them to the decisions underneath, and let drift surface.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a technical design document (TDD)?', a: 'A technical design document describes how you will build something before you build it — the architecture, the data model, the API design, the key trade-offs, and the risks. It exists so the hard decisions are made and reviewed on paper, where they are cheap to change, instead of discovered halfway through the implementation.' },
            { q: 'What should a technical design document include?', a: 'Overview, goals and non-goals, proposed architecture, data model, API design, key trade-offs, risks and mitigations, and a rollout plan. Non-goals and trade-offs are the sections that age best: they record what you deliberately ruled out, so nobody reopens a settled question six months later.' },
            { q: 'What is the difference between a technical design document and a PRD?', a: 'A PRD says what to build and why — the problem, the users, the requirements. A technical design document says how to build it — the architecture and the engineering decisions that satisfy the PRD. The PRD is the input; the TDD is the engineering answer to it.' },
            { q: 'How do you keep a design doc from drifting from the code?', a: 'Re-syncing a design doc by hand loses to shipping every time. Draftlize links each section to the PRD lines and decisions it rests on, so when one moves the design card flips to drift_detected — the doc tells you it is out of sync the moment it is, and a coding agent reads the corrected design instead of the one that matched the previous PRD.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your design doc <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or spin up a project, link your TDD sections to the PRD and decisions they rest on, and connect your coding agent over MCP — so the design stays in sync with the spec, and flags drift the moment a requirement moves.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
